import { useState, useRef, useEffect, useCallback } from 'react';
import PQueue from 'p-queue';
import { sleep } from './helpers';

const useHandleSWR = (swrData = undefined, defaultData = null, defaultBusy = false) => {
  const [ data, setData ] = useState(defaultData);
  const [ error, setError ] = useState(null);
  const [ busy, setBusy ] = useState(defaultBusy);
  const [ total, setTotal ] = useState(0);

  useEffect(() => {
    if (swrData !== undefined) { 
      if (swrData.success) {
        setError(null);
        setData(swrData.data);
        setTotal(swrData.total ? swrData.total : 0);
      }
      else {
        setError(swrData.error);
      }
    }
    setBusy(swrData === undefined);
  }, [ swrData ]);

  return { busy, data, total, error };
}

// Sometimes, we need to align or place elements depending on other dynamic elements.
// This hook returns a ref (which should be assign to a component), and the size of
// this element every time it changes.

const useComponentSize = () => {
  const ref = useRef();
  const [ size, setSize ] = useState({});

  useEffect(() => {
    if (ref && ref.current) {
      const observer = new ResizeObserver(entries => {
        let element = entries[0];
        if (element.contentRect) {
          setSize({ 
            width: element.contentRect.left + element.contentRect.right, 
            height: element.contentRect.top + element.contentRect.bottom 
          });
        }
      });
      observer.observe(ref.current);
      return () => { observer.unobserve(ref.current); };
    }
  }, []);
  return { ref, width: size.width ? size.width : 0, height: size.height ? size.height : 0 };
};

// If a click is made outside the component, then the function onOutsideClick is called.
// This function returns a ref which needs to be assigned to the component.

const useOutsideClick = (onOutsideClick) => {
  const componentRef = useRef();
  const handleOutsideClick = (e) => {
    if (!onOutsideClick || componentRef.current.contains(e.target)) {
      return;
    }
    onOutsideClick();
  };
  useEffect(() => {
    document.addEventListener("mousedown", handleOutsideClick);
    return () => { document.removeEventListener("mousedown", handleOutsideClick); };
  });
  return componentRef;
};


// This hook allows calling many requests without having too many of them
// running concurrently.

let GlobalQueue = null;

const useNekoGlobalQueue = (params = {}) => {

  if (!GlobalQueue) {
    let { concurrency = 2 } = params;
    GlobalQueue = new PQueue({ concurrency, autoStart: true });
  }

  const runTask = async (func) => {
    return await GlobalQueue.add(func);
  }

  return { runTask };
};

// This hook handles batch of asynchronous requests, and provide everything
// the NekoProgress might need.

const isDebug = true;
const UploadQueue = new PQueue({ concurrency: 1, autoStart: false });

const useNekoTasks = () => {

  // We should be able to kill fetch requests using this
  // Interesting article: https://davidwalsh.name/cancel-fetch
  const [ controller, setController ] = useState(new AbortController());
  const refSkip = useRef(false);
  const refErrors = useRef(0);
  const refCurrentTask = useRef(null);
  const refRetries = useRef(0);
  const refRetriesLeft = useRef(0);
  const [ isSleeping, setIsSleeping ] = useState(false);
  const [ busy, setBusy ] = useState(null);
  const [ paused, setPaused ] = useState(false);
  const [ max, setMax ] = useState(0);
  const [ error, setError ] = useState(false);
  const [ success, setSuccess ] = useState(false);
  const [ value, setValue ] = useState(0);

  const reset = async () => {
    let newController = new AbortController();
    setController(newController);
    setError(false);
    refErrors.current = 0;
    refSkip.current = false;
    setPaused(false);
    setSuccess(false);
    setValue(0);
    setMax(0);
  };

  const start = async (tasks) => {
    const runnerPromise = new Promise((resolve, reject) => {
      setError(false);
      refRetries.current = 0;
      refRetriesLeft.current = 0;
      refErrors.current = 0;
      refSkip.current = false;
      setPaused(false);
      setSuccess(false);
      setTasks(tasks);
      setBusy(true);
      if (isDebug) { console.log('[useNekoTasks] Start.'); }
      UploadQueue.start();
      UploadQueue.onIdle().then(() => {
        onIdle();
        resolve();
      }); 
    });
    return runnerPromise;
  };

  const pause = async () => {
    if (isDebug) { console.log('[useNekoTasks] Pause.'); }
    UploadQueue.pause();
    setPaused(true);
  };

  const resume = async () => {
    if (isDebug) { console.log('[useNekoTasks] Resume.'); }
    setError(false);
    setPaused(false);
    UploadQueue.start();
  };

  const autoRetry = async (maxRetries = 5) => {
    refRetries.current = maxRetries;
    refRetriesLeft.current = maxRetries;
    retry();
  }

  const retry = async () => {
    if (isDebug) { console.log('[useNekoTasks] Retrying.'); }
    setError(false);
    setPaused(false);
    if (refRetriesLeft.current > 0) {
      if (refRetriesLeft.current < refRetries.current) {
        let retries = refRetries.current - refRetriesLeft.current;
        setIsSleeping(true);
        let sleepFor = retries * 5000;
        if (isDebug) {
          console.log(`[useNekoTasks] Sleep for ${sleepFor}ms.`);
        }
        await sleep(sleepFor);
        setIsSleeping(false);
      }
      refRetriesLeft.current--;
    }
    if (refCurrentTask.current) {
      await executeTask(refCurrentTask.current, true);
    }
    //UploadQueue.start();
  };

  const stop = async () => {
    if (isDebug) { console.log('[useNekoTasks] Cancel.'); }
    UploadQueue.pause();
    controller.abort();
    setBusy(false);
    setError(false);
    setSuccess(false);
  };

  const onIdle = async () => {
    if (isDebug) { console.log('[useNekoTasks] onIdle.'); }
    setSuccess(true);
    setBusy(false);
  };

  const executeTask = async(task, isRetry = false) => {
    try {
      if (isRetry) {
        refErrors.current--;
        setValue(value => value - 1);
      }
      refCurrentTask.current = task;
      let res = await task(controller.signal);
      if (!res || !res.success) {
        console.error('[useNekoTasks] The function for that task did not return anything.', res);
      }
      if (!res.success) {
        refErrors.current++;

        // Otherwise, if "auto-skip" is not set, we set an error and break.
        if (!refSkip.current) {
          pause();
          // If there are retries left, let's retry.
          if (refRetriesLeft.current > 0) {
            retry();
            return;
          }
          else {
            setError(res.error);
          }
        }
      }
      // If we are retrying a task and it is successful, we should restart PQueue
      else if (isRetry) {
        refRetriesLeft.current = refRetries.current;
        UploadQueue.start();
      }
    }
    catch (err) {
      if (err.name === 'AbortError') {
        console.log('[useNekoTasks] Aborted');
        return;
      }
      else {
        refErrors.current++;
        if (!refSkip.current) {
          setError("[useNekoTasks] An error was caught (more information in the console).");
          pause();
        }
      }
    }
    finally {
      setValue(value => value + 1);
    }
  }

  const addTask = async (task) => {
    UploadQueue.add(async () => executeTask(task));
    setMax(max => max + 1);
  }

  const setTasks = async (tasks) => {
    UploadQueue.clear();
    for (let task of tasks) {
      addTask(task);
    }
    setValue(0);
  };

  const setAlwaysSkip = useCallback((value = true) => {
    refSkip.current = true;
  }, []);

  const getErrorCount = useCallback(() => {
    return refErrors.current;
  }, []);

  return { start, stop, pause, resume, reset, retry, autoRetry, isSleeping,
    addTask, setAlwaysSkip, getErrorCount,
    error, success, busy, paused, value, max };
};

const useKeyboardEvent = () => {
	const [ pressShift, setPressShift ] = useState(false);
  const [ pressControl, setPressControl ] = useState(false);

	const keydownFunction = useCallback((e) => {
		setPressShift(e.shiftKey);
    setPressControl(e.ctrlKey || e.metaKey);
	}, []);
	const keyupFunction = useCallback(() => {
		setPressShift(false);
    setPressControl(false);
	}, []);

	useEffect(() => {
		document.addEventListener("keydown", keydownFunction, false);
		document.addEventListener("keyup", keyupFunction, false);

		return () => {
		document.removeEventListener("keydown", keydownFunction, false);
		document.removeEventListener("keyup", keyupFunction, false);
		}
	}, []);

	return { pressShift, pressControl };
}

export { useHandleSWR, useOutsideClick, useNekoGlobalQueue, useNekoTasks, useComponentSize, useKeyboardEvent };